// UI Animations - GSAP-powered game-quality animations
import gsap from 'gsap';

// Easing presets for game-like feel
const EASING = {
    bounce: 'elastic.out(1, 0.3)',
    softBounce: 'back.out(1.7)',
    smooth: 'power2.out',
    snap: 'power3.out',
    anticipate: 'power2.inOut',
};

// Animation durations
const DURATION = {
    fast: 0.15,
    normal: 0.3,
    slow: 0.5,
    dramatic: 0.8,
};

export class UIAnimations {
    private static particleContainer: HTMLDivElement | null = null;

    static init(container: HTMLElement): void {
        // Create particle container
        this.particleContainer = document.createElement('div');
        this.particleContainer.className = 'particle-container';
        this.particleContainer.style.cssText = `
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            z-index: 1000;
            overflow: hidden;
        `;
        container.appendChild(this.particleContainer);
    }



    /**
     * Quest modal slide-in with staggered options
     */
    static questPanelEnter(
        panel: HTMLElement,
        title: HTMLElement,
        message: HTMLElement,
        options: HTMLElement[],
        onComplete?: () => void
    ): gsap.core.Timeline {
        const tl = gsap.timeline({ onComplete });

        // Initial states
        gsap.set(panel, {
            x: 100,
            opacity: 0,
            scale: 0.9,
        });
        gsap.set(title, { y: -20, opacity: 0 });
        gsap.set(message, { opacity: 0 });
        gsap.set(options, { x: 50, opacity: 0, scale: 0.8 });

        // Sequence
        tl.to(panel, {
            x: 0,
            opacity: 1,
            scale: 1,
            duration: DURATION.normal,
            ease: EASING.softBounce,
        })
            .to(title, {
                y: 0,
                opacity: 1,
                duration: DURATION.fast,
                ease: EASING.snap,
            }, '-=0.1')
            .to(message, {
                opacity: 1,
                duration: DURATION.fast,
            }, '-=0.1')
            // Options cascade with stagger
            .to(options, {
                x: 0,
                opacity: 1,
                scale: 1,
                duration: DURATION.normal,
                ease: EASING.softBounce,
                stagger: 0.08,
            }, '-=0.1');

        return tl;
    }

    /**
     * Option selection with celebration
     */
    static optionSelect(
        option: HTMLElement,
        allOptions: HTMLElement[],
        onComplete: () => void
    ): gsap.core.Timeline {
        const tl = gsap.timeline({ onComplete });

        // Get option position for particles
        const rect = option.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        // Pulse the selected option
        tl.to(option, {
            scale: 1.05,
            duration: DURATION.fast,
            ease: EASING.anticipate,
        })
            .to(option, {
                scale: 1,
                backgroundColor: '#ffcce0',
                borderColor: '#ff6b9d',
                duration: DURATION.fast,
            })
            // Create celebration particles
            .call(() => {
                this.createConfetti(centerX, centerY, 15);
            })
            // Fade out other options
            .to(allOptions.filter(o => o !== option), {
                opacity: 0.3,
                scale: 0.95,
                duration: DURATION.fast,
            }, '-=0.2')
            // Brief pause before transitioning
            .to({}, { duration: 0.3 });

        return tl;
    }

    /**
     * Quest panel exit animation
     */
    static questPanelExit(
        panel: HTMLElement,
        onComplete: () => void
    ): gsap.core.Timeline {
        const tl = gsap.timeline({ onComplete });

        tl.to(panel, {
            x: -100,
            opacity: 0,
            scale: 0.9,
            duration: DURATION.normal,
            ease: EASING.anticipate,
        });

        return tl;
    }

    /**
     * Chapter complete celebration
     */
    static chapterComplete(
        banner: HTMLElement,
        onComplete: () => void
    ): gsap.core.Timeline {
        const tl = gsap.timeline({ onComplete });

        gsap.set(banner, {
            y: -200,
            opacity: 0,
            scaleY: 0.2,
        });

        // Sparkle explosion
        this.createSparkles(window.innerWidth / 2, 100, 30);

        tl.to(banner, {
            y: 0,
            opacity: 1,
            scaleY: 1,
            duration: DURATION.slow,
            ease: EASING.bounce,
        })
            .call(() => {
                this.createConfetti(window.innerWidth / 2, 200, 40);
                this.createStars(window.innerWidth / 2, 150, 20);
            });

        return tl;
    }

    /**
     * Button hover effect
     */
    static buttonHover(button: HTMLElement): void {
        gsap.to(button, {
            scale: 1.05,
            y: -2,
            duration: DURATION.fast,
            ease: EASING.smooth,
        });
    }

    /**
     * Button unhover effect
     */
    static buttonUnhover(button: HTMLElement): void {
        gsap.to(button, {
            scale: 1,
            y: 0,
            duration: DURATION.fast,
            ease: EASING.smooth,
        });
    }

    /**
     * Button press effect
     */
    static buttonPress(button: HTMLElement): void {
        gsap.to(button, {
            scale: 0.95,
            duration: 0.05,
            ease: 'power1.in',
            onComplete: () => {
                gsap.to(button, {
                    scale: 1,
                    duration: 0.1,
                    ease: EASING.bounce,
                });
            },
        });
    }

    /**
     * Typewriter effect for text
     */
    static typewriter(
        element: HTMLElement,
        text: string,
        speed: number = 30
    ): gsap.core.Timeline {
        const tl = gsap.timeline();
        element.textContent = '';

        const chars = text.split('');
        chars.forEach((char, i) => {
            tl.call(() => {
                element.textContent += char;
            }, [], i * (speed / 1000));
        });

        return tl;
    }

    /**
     * Create sparkle particles
     */
    static createSparkles(x: number, y: number, count: number): void {
        if (!this.particleContainer) return;

        for (let i = 0; i < count; i++) {
            const particle = document.createElement('div');
            particle.innerHTML = '✨';
            particle.style.cssText = `
                position: absolute;
                left: ${x}px;
                top: ${y}px;
                font-size: ${12 + Math.random() * 16}px;
                pointer-events: none;
            `;
            this.particleContainer.appendChild(particle);

            const angle = (i / count) * Math.PI * 2;
            const distance = 50 + Math.random() * 100;

            gsap.to(particle, {
                x: Math.cos(angle) * distance,
                y: Math.sin(angle) * distance - 50,
                opacity: 0,
                scale: 0,
                rotation: Math.random() * 360,
                duration: 0.8 + Math.random() * 0.5,
                ease: 'power2.out',
                onComplete: () => { particle.remove(); },
            });
        }
    }



    /**
     * Create confetti particles
     */
    static createConfetti(x: number, y: number, count: number): void {
        if (!this.particleContainer) return;

        const colors = ['#ff6b9d', '#ffd700', '#4fc3f7', '#81c784', '#ba68c8', '#ffb74d'];

        for (let i = 0; i < count; i++) {
            const particle = document.createElement('div');
            const color = colors[Math.floor(Math.random() * colors.length)];
            particle.style.cssText = `
                position: absolute;
                left: ${x}px;
                top: ${y}px;
                width: ${4 + Math.random() * 6}px;
                height: ${8 + Math.random() * 8}px;
                background: ${color};
                border-radius: 2px;
                pointer-events: none;
            `;
            this.particleContainer.appendChild(particle);

            const angle = Math.random() * Math.PI * 2;
            const distance = 50 + Math.random() * 150;
            const gravity = 200 + Math.random() * 100;

            gsap.to(particle, {
                x: Math.cos(angle) * distance,
                y: Math.sin(angle) * distance + gravity,
                opacity: 0,
                rotation: Math.random() * 720 - 360,
                duration: 1 + Math.random() * 0.8,
                ease: 'power1.out',
                onComplete: () => { particle.remove(); },
            });
        }
    }

    /**
     * Create star particles
     */
    static createStars(x: number, _y: number, count: number): void {
        if (!this.particleContainer) return;

        for (let i = 0; i < count; i++) {
            const particle = document.createElement('div');
            particle.innerHTML = '⭐';
            particle.style.cssText = `
                position: absolute;
                left: ${x + (Math.random() - 0.5) * 200}px;
                top: -50px;
                font-size: ${14 + Math.random() * 14}px;
                pointer-events: none;
            `;
            this.particleContainer.appendChild(particle);

            const delay = i * 0.05;

            gsap.to(particle, {
                y: window.innerHeight + 100,
                x: (Math.random() - 0.5) * 100,
                rotation: Math.random() * 360,
                duration: 1.5 + Math.random() * 1,
                delay,
                ease: 'power1.in',
                onComplete: () => { particle.remove(); },
            });
        }
    }

    /**
     * Screen shake effect
     */
    static screenShake(element: HTMLElement, intensity: number = 5): void {
        const tl = gsap.timeline();

        for (let i = 0; i < 5; i++) {
            tl.to(element, {
                x: (Math.random() - 0.5) * intensity * 2,
                y: (Math.random() - 0.5) * intensity * 2,
                duration: 0.05,
            });
        }

        tl.to(element, {
            x: 0,
            y: 0,
            duration: 0.1,
        });
    }

    /**
     * Pulse glow effect
     */
    static pulseGlow(element: HTMLElement): gsap.core.Tween {
        return gsap.to(element, {
            boxShadow: '0 0 20px rgba(255, 107, 157, 0.6)',
            duration: 0.8,
            repeat: -1,
            yoyo: true,
            ease: 'sine.inOut',
        });
    }

    /**
     * Kill a glow animation
     */
    static stopGlow(element: HTMLElement): void {
        gsap.killTweensOf(element);
        gsap.set(element, { boxShadow: 'none' });
    }
}
